import React from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { getCountryName } from '../../utils/countries';
import { FileText, Download, Eye } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { formatBytes } from '../../context/DownloadContext';

const PUBLIC_BASE_URL = process.env.REACT_APP_PUBLIC_BASE_URL || window.location.origin;

export const buildQrUrl = (qrToken) => `${PUBLIC_BASE_URL}/public/qr/${qrToken}`;

const Section = ({ title, rows }) => (
  <div className="mb-4">
    <h3 className="text-sm font-semibold text-slate-700 border-b border-slate-200 pb-1 mb-2 print:text-black">{title}</h3>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
      {rows.filter(([, v]) => v).map(([label, value], i) => (
        <div key={i} className="flex flex-col sm:flex-row">
          <span className="text-xs text-slate-500 sm:w-40 sm:shrink-0 print:text-gray-600">{label}:</span>
          <span className="text-xs text-slate-800 print:text-black break-words overflow-wrap-anywhere">{value}</span>
        </div>
      ))}
    </div>
  </div>
);

export const ProductPrintView = ({ product, project, user, onDownloadSubmittal, onPreviewSubmittal, showActions = false }) => {
  if (!product) return null;
  const qrUrl = product.qrToken ? buildQrUrl(product.qrToken) : '';
  const mp = product.materialProperties || {};
  const tp = product.thermalPerformance || {};
  const fc = product.fireCompliance || {};
  const mo = product.moistureProperties || {};
  const ip = product.installationParameters || {};
  const ir = product.interfaceResponsibility || {};
  const fv = product.fieldVerification || {};
  const subs = product.submittals || [];
  const proj = project || product.project || {};
  const usr = user || product.viewedBy || {};

  return (
    <div className="print-view bg-white p-4 sm:p-6 max-w-3xl mx-auto" data-testid="product-print-view">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-6 pb-4 gap-3">
        <div className="flex-1">
          <h1 className="text-lg font-bold text-slate-900">{product.productNameModel}</h1>
          <p className="text-xs text-slate-500 mt-1">{product.manufacturerName || ''} {product.countryCode ? `| ${getCountryName(product.countryCode)}` : ''}</p>
          <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium mt-2 ${product.status === 'active' ? 'bg-green-600 text-white' : 'bg-amber-100 text-amber-700'}`}>{product.status || 'draft'}</span>
        </div>
        {qrUrl && (
          <div className="flex flex-col items-center ml-4 shrink-0" data-testid="print-qr-code">
            <QRCodeSVG value={qrUrl} size={80} level="L" />
            <p className="text-[8px] text-slate-400 mt-1 text-center max-w-[80px] break-all">Specard QR</p>
          </div>
        )}
      </div>

      {/* Project Info */}
      <Section title="Project Information" rows={[
        ['Project Name', proj.name],
        ['Project Code', proj.projectCode],
        ['Address', proj.address],
        ['Owner', proj.owner],
      ]} />

      {/* Product Sections */}
      <Section title="Material & Physical Properties" rows={[
        ['Material Type', mp.materialType === 'Other' ? `Other: ${mp.materialTypeOther}` : mp.materialType],
        ['Thickness', mp.thickness ? `${mp.thickness} in` : null],
        ['Density', mp.density],
        ['Compressive Strength', mp.compressiveStrength ? `${mp.compressiveStrength} psi` : null],
        ['Facing Type', mp.facingType],
      ]} />

      <Section title="Thermal Performance" rows={[
        ['R-Value', tp.declaredRValue],
        ['U-Value', tp.calculatedUValue],
        ['Test Standard', tp.testStandard],
        ['Temperature Condition', tp.temperatureCondition ? `${tp.temperatureCondition} °F` : null],
        ['CR', tp.cr],
      ]} />

      <Section title="Fire, Smoke & Code Compliance" rows={[
        ['Fire Rating', fc.fireRating],
        ['ASTM E84', fc.astmE84],
        ['NFPA / UL Reference', fc.nfpaReference],
        ['ICC-ES Report', fc.iccESReport],
      ]} />

      <Section title="Moisture & Vapor Properties" rows={[
        ['Water Absorption', mo.waterAbsorption ? `${mo.waterAbsorption}%` : null],
        ['Vapor Permeance', mo.vaporPermeance ? `${mo.vaporPermeance} perms` : null],
        ['ASTM E96 Method', mo.astmE96Method],
        ['Application Area', (mo.applicationArea || []).join(', ') || mo.applicationType],
      ]} />

      <Section title="Installation Parameters" rows={[
        ['Attachment Method', ip.attachmentMethod === 'Other' ? `Other: ${ip.attachmentMethodOther}` : ip.attachmentMethod],
        ['Substrate Type', ip.substrateType],
        ['Max Fastener Spacing', ip.maxFastenerSpacing],
        ['Install Temp Range', ip.installationTempRange],
        ['UV Exposure Limit', ip.uvExposureLimit ? `${ip.uvExposureLimit} days` : null],
      ]} />

      <Section title="Interface & Responsibility" rows={[
        ['Adjacent Systems', (ir.adjacentSystems || []).join(', ')],
        ['Interface Responsibility', ir.interfaceResponsibility],
        ...(ir.interfaceResponsibility === 'Other' ? [['Other Subcontractor', ir.otherSubcontractor]] : []),
        ['Transition Membrane', ir.transitionMembraneRequired],
      ]} />

      {/* Field Verification */}
      {(fv.installedThickness || fv.nonConformance || fv.inspectorComments || fv.installationPhotos?.length > 0) && (
        <div className="mb-4">
          <Section title="Field Verification" rows={[
            ['Installed Thickness', fv.installedThickness ? `${fv.installedThickness} in` : null],
            ['Non-conformance', fv.nonConformance ? (fv.nonConformance === 'Yes' ? 'Yes' : 'No') : null],
            ['Inspector Comments', fv.inspectorComments || null],
            ['Installation Photos', fv.installationPhotos?.length ? `${fv.installationPhotos.length} photo(s)` : null],
          ]} />
          {fv.installationPhotos?.length > 0 && (
            <div className="flex gap-2 mt-1 flex-wrap">
              {fv.installationPhotos.map((p, i) => (
                <img key={i} src={p.url} alt={p.fileName} className="w-20 h-20 object-cover rounded border" />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Submittals */}
      {subs.length > 0 && (
        <div className="mb-4">
          <h3 className="text-sm font-semibold text-slate-700 border-b border-slate-200 pb-1 mb-2 print:text-black">Submittals ({subs.length})</h3>
          <div className="space-y-1.5">
            {subs.map((s, i) => (
              <div key={s.id || i} className="flex items-center gap-2 text-xs" data-testid={`print-submittal-${i}`}>
                <FileText className="h-3.5 w-3.5 text-slate-400 shrink-0 print:text-gray-500" />
                <span className="text-slate-600 w-36 shrink-0">{s.documentType}</span>
                <span className="text-slate-800 flex-1 truncate">{s.fileName}</span>
                <span className="text-slate-400 shrink-0">{formatBytes(s.bytes)}</span>
                {showActions && onPreviewSubmittal && (
                  <button onClick={() => onPreviewSubmittal(s)} className="p-0.5 rounded text-slate-400 hover:bg-orange-500 hover:text-white transition-colors print:hidden" title="Preview" data-testid={`print-preview-${i}`}>
                    <Eye className="h-3.5 w-3.5" />
                  </button>
                )}
                {showActions && onDownloadSubmittal && (
                  <button onClick={() => onDownloadSubmittal(s.id, s.fileName, s.bytes)} className="p-0.5 rounded text-slate-400 hover:bg-orange-500 hover:text-white transition-colors print:hidden" title="Download" data-testid={`print-download-${i}`}>
                    <Download className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="border-t pt-3 mt-4 flex justify-between text-[10px] text-slate-400 print:text-gray-500">
        <span>Printed by: {usr.name || usr.email || 'Unknown'} ({usr.email || ''})</span>
        <span>{new Date().toLocaleString()}</span>
      </div>
    </div>
  );
};

export const QRThumbnail = ({ qrToken, size = 32 }) => {
  if (!qrToken) return <div className="bg-slate-100 rounded" style={{ width: size, height: size }} />;
  return <QRCodeSVG value={buildQrUrl(qrToken)} size={size} level="L" />;
};
